/**
 * The empty slot at the head of a collection, with a plus in it.
 *
 * A familiar pattern, and it earns its place twice over. For a garden that
 * already has plants it is the one offer to add another, in the place a new
 * one will appear, rather than a button in a toolbar that scrolls away. For a
 * garden with none it is the whole screen: a single obvious target rather
 * than a blank area and a sentence explaining that it is blank.
 *
 * It led from the end for a while, on the reasoning that the eye is already
 * there once it has read the garden. That is true of a garden you have read
 * and false of one you are scrolling through looking for something, and it
 * put the control furthest away exactly when there was most to scroll.
 *
 * It takes the shape of whatever it sits among — a card among cards, a square
 * among tiles, a row among rows — because a placeholder that ignores the
 * layout reads as a mistake rather than an invitation. The dashed outline is
 * the part that says "not a thing yet"; everything else matches its
 * neighbours.
 *
 * Used by: the plant and bed grids (PlantGrid, LocationGrid).
 *
 * @module AddPlaceholder
 */

import React from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Which neighbours the placeholder sits among: cards, grid tiles or list rows. */
export type AddPlaceholderShape = 'card' | 'tile' | 'row';

/** Its shape, its words, and what pressing it does. */
interface AddPlaceholderProps {
  /** Which neighbours it has to live among. */
  shape: AddPlaceholderShape;
  /** What it makes — "Add plant", "Add bed". Read out as the button's name. */
  label: string;
  /** What pressing it does: open the add dialog. */
  onClick: () => void;
}

/**
 * Shared look: dashed, muted, and unmistakably interactive on hover.
 *
 * Kept in one string so the three shapes cannot drift apart. Only the box
 * differs between them; the behaviour and the colours do not.
 */
const SHELL = 'group flex items-center justify-center gap-2 rounded-sm border-2 border-dashed '
  + 'border-muted-foreground/30 text-muted-foreground transition-colors '
  + 'hover:border-primary hover:text-primary hover:bg-primary/5 '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/** A dashed "+ Add â€¦" slot in the shape of its neighbours. */
export const AddPlaceholder: React.FC<AddPlaceholderProps> = ({ shape, label, onClick }) => {
  if (shape === 'row') {
    return (
      <button type="button" onClick={onClick} aria-label={label}
        /* One dashed rule, below, and nothing doubled either side of it.
           
           The container is `divide-y border`: it draws its own line above the
           first child and, through divide-y, a top border on every child after
           it. So a dashed top edge here sits under the container's border, and
           simply moving the dash to the bottom puts it against the next row's
           divide line instead — the same two-lines-where-there-should-be-one,
           relocated. The sibling's top border is suppressed as well, which is
           what actually removes it — and with !important, because divide-y's
           own selector (`& > :not([hidden]) ~ :not([hidden])`) carries three
           classes of specificity against this one and wins without it. */
        className={cn(SHELL, 'w-full border-x-0 border-t-0 px-4 py-4 [&+*]:!border-t-0')}
      >
        <Plus className="h-5 w-5" aria-hidden="true" />
        <span className="font-medium">{label}</span>
      </button>
    );
  }

  if (shape === 'tile') {
    return (
      // Height comes from the grid, not from here. A tile is a square picture
      // with a name and a date under it, and the total depends on how those
      // wrap — so `h-full` lets the row it lands in set the height and the
      // placeholder matches its neighbours exactly. The minimum only matters
      // on a row of its own, where there is nothing to match.
      <button type="button" onClick={onClick} aria-label={label}
        className={cn(SHELL, 'h-full min-h-[120px] w-full flex-col gap-1.5')}
      >
        <Plus className="h-8 w-8" aria-hidden="true" />
        <span className="px-2 text-center text-sm font-medium">{label}</span>
      </button>
    );
  }

  return (
    // A card among cards: the same width, but a slim bar rather than a box
    // the height of a picture. At 220px it pushed the first plant half a
    // phone down the page to offer one button.
    <button type="button" onClick={onClick} aria-label={label}
      className={cn(SHELL, 'min-h-14 w-full max-w-sm px-4 py-3')}
    >
      <Plus className="h-5 w-5" aria-hidden="true" />
      <span className="font-medium">{label}</span>
    </button>
  );
};
